/**
 * OPS-11: Operational Pressure Panel
 *
 * Displays real-time operational workload derived from shift briefing
 * highlights (OPS-9 → OPS-5 metrics) and alert signals (OPS-7).
 *
 * Semantically distinct from Dimension Health (which shows pulse scores).
 * This panel answers: "How heavy is the current workload?"
 *
 * Pure frontend derivation from existing ControlTowerState — no new APIs.
 */

import { useMemo, memo } from 'react';
import { InfoTooltip } from './InfoTooltip';
import type {
  ShiftHighlight,
  OperationalAlert,
} from '../../services/controlTowerTypes';

// ─── Pressure Model ──────────────────────────────────────────────────────────

type PressureLevel = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

interface PressureIndicator {
  id: string;
  label: string;
  icon: string;
  level: PressureLevel;
  fillPercent: number;
}

const LEVEL_CONFIG: Record<PressureLevel, { bar: string; text: string; bg: string }> = {
  LOW:      { bar: 'bg-emerald-400', text: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  MEDIUM:   { bar: 'bg-amber-400',   text: 'text-amber-400',   bg: 'bg-amber-500/10' },
  HIGH:     { bar: 'bg-orange-400',   text: 'text-orange-400',  bg: 'bg-orange-500/10' },
  CRITICAL: { bar: 'bg-rose-400',     text: 'text-rose-400',    bg: 'bg-rose-500/10' },
};

const FILL_BY_LEVEL: Record<PressureLevel, number> = {
  LOW: 25, MEDIUM: 50, HIGH: 75, CRITICAL: 95,
};

// ─── Pressure Derivation ─────────────────────────────────────────────────────

/** Maps highlight types from OPS-9 (backed by OPS-5 metrics) to pressure categories */
const HIGHLIGHT_PRESSURE_MAP: Record<string, string> = {
  OPEN_REQUESTS: 'requests',
  HIGH_RESPONSE_TIME: 'requests',
  REQUEST_SURGE: 'requests',
  HOUSEKEEPING_BACKLOG: 'housekeeping',
  MAINTENANCE_LOAD: 'maintenance',
  CAPACITY_PRESSURE: 'capacity',
  SLA_BREACHES: 'capacity',
};

/** Maps alert types from OPS-7 to pressure categories */
const ALERT_PRESSURE_MAP: Record<string, string> = {
  RESPONSE_DELAY_RISK: 'requests',
  REQUEST_SURGE: 'requests',
  HOUSEKEEPING_OVERLOAD: 'housekeeping',
  MAINTENANCE_ESCALATION: 'maintenance',
  WORKLOAD_CRITICAL: 'capacity',
  SLA_BREACH_RISK: 'capacity',
};

function severityWeight(severity: string): number {
  if (severity === 'critical') return 3;
  if (severity === 'warning') return 2;
  return 1;
}

function scoreToPressure(score: number): PressureLevel {
  if (score >= 5) return 'CRITICAL';
  if (score >= 3) return 'HIGH';
  if (score >= 1) return 'MEDIUM';
  return 'LOW';
}

function derivePressure(
  highlights: ShiftHighlight[],
  alerts: OperationalAlert[],
): PressureIndicator[] {
  // Accumulate weighted pressure per category
  const scores: Record<string, number> = {
    requests: 0,
    housekeeping: 0,
    maintenance: 0,
    capacity: 0,
  };

  for (const h of highlights) {
    const cat = HIGHLIGHT_PRESSURE_MAP[h.type];
    if (cat) scores[cat]! += severityWeight(h.severity);
  }

  for (const a of alerts) {
    const cat = ALERT_PRESSURE_MAP[a.type];
    if (cat) scores[cat]! += severityWeight(a.severity);
  }

  const indicators: PressureIndicator[] = [
    { id: 'requests',     label: 'Request Backlog',    icon: '📋' },
    { id: 'housekeeping', label: 'Housekeeping Load',  icon: '🏨' },
    { id: 'maintenance',  label: 'Maintenance Load',   icon: '🔧' },
    { id: 'capacity',     label: 'Staff Capacity',     icon: '👥' },
  ].map((base) => {
    const level = scoreToPressure(scores[base.id]!);
    return { ...base, level, fillPercent: FILL_BY_LEVEL[level] };
  });

  return indicators;
}

// ─── Component ───────────────────────────────────────────────────────────────

interface OperationalPressurePanelProps {
  highlights: ShiftHighlight[];
  alerts: OperationalAlert[];
}

export const OperationalPressurePanel = memo(function OperationalPressurePanel({ highlights, alerts }: OperationalPressurePanelProps) {
  const indicators = useMemo(
    () => derivePressure(highlights, alerts),
    [highlights, alerts],
  );

  return (
    <div className="min-h-[160px] rounded-2xl bg-slate-800/50 border border-slate-700/50 p-5 shadow-lg">
      <div className="mb-4">
        <h2 className="text-sm font-medium text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
          Operational Pressure
          <InfoTooltip text="Operational pressure measures current workload stress across requests, housekeeping, maintenance, and capacity — helping you anticipate bottlenecks before they affect guests." />
        </h2>
        <p className="text-[10px] text-slate-500 mt-0.5">Real-time operational workload</p>
      </div>

      <div className="space-y-3">
        {indicators.map((ind) => {
          const cfg = LEVEL_CONFIG[ind.level];
          return (
            <div key={ind.id}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm">{ind.icon}</span>
                  <span className="text-xs text-slate-300 font-medium">{ind.label}</span>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wide ${cfg.text}`}>
                  {ind.level}
                </span>
              </div>
              <div
                className="h-2 bg-slate-700/50 rounded-full overflow-hidden"
                role="meter"
                aria-label={`${ind.label} pressure`}
                aria-valuenow={ind.fillPercent}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className={`h-full rounded-full transition-all duration-700 ease-out ${cfg.bar}`}
                  style={{ width: `${ind.fillPercent}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});
